/**
 * AI CONTEXT
 * Purpose: shared quick-notes widget (global operational notes collection).
 * Reads/writes: quick_notes collection with pending/completed/deleted states.
 * Used by: SuperView/AdminView side panels.
 * Importance: cross-user scratchpad/task-lite workflow.
 * Refactor: replace motion invariant import with project invariant utility for consistency.
 */
import { orderBy, partition } from "lodash"
import { invariant, motion } from "motion/react"
import { useMemo } from "react"

import { quickNotesAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { type QuickNotes } from "#customTypes/composedTypes/quickNotesTypes"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import useIsSuperView from "#views/SuperView/useIsSuperView"

import QuickNoteListItem from "./QuickNoteListItem"
import QuickNotesInput from "./QuickNotesInput"

// If we ever want editable quick notes, try Typography.Text or TextInputToggle
const QuickNotes = () => {
  const isSuperView = useIsSuperView()
  const theme = useThemeContext()
  const { currentUserId } = useUserContext()

  const query: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        deletedAt: {
          value: null,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    []
  )

  const quickNotes = useFilteredDocs<QuickNotes>(
    quickNotesAtoms,
    DOC_TYPES.QUICK_NOTES,
    query
  )

  const sortedQuickNotes = useMemo(() => {
    const activeNotes = orderBy(
      quickNotes.filter((note) => note.deletedAt === null),
      ["createdAt"],
      ["desc"]
    )
    const [completed, pending] = partition(activeNotes, "completedAt")

    return { completed, pending }
  }, [quickNotes])
  const { pending, completed } = sortedQuickNotes

  const handleSubmit = async (newNote: string): Promise<string> => {
    invariant(currentUserId != null, "Error finding current user")

    const id = await Firebase.getInstance().addDocument<QuickNotes>(
      DOC_TYPES.QUICK_NOTES,
      {
        description: newNote,
        createdBy: currentUserId,
        createdAt: new Date().toISOString(),
        completedAt: null,
        completedBy: null,
        deletedAt: null,
        deletedBy: null,
      }
    )

    return id
  }

  const handleDelete = (id: string) => {
    Firebase.getInstance().updateDocument(DOC_TYPES.QUICK_NOTES, id, {
      deletedAt: new Date().toISOString(),
      deletedBy: currentUserId,
    })
  }

  const handleComplete = (id: string) => {
    invariant(
      currentUserId != null,
      "Error completing quick note: no current user id found."
    )

    Firebase.getInstance().updateDocument(DOC_TYPES.QUICK_NOTES, id, {
      completedAt: new Date().toISOString(),
      completedBy: currentUserId,
    })
  }

  return (
    <Flex
      col
      style={{
        width: isSuperView ? "100%" : 320,
        overflow: "hidden",
        border: "1px solid black",
        borderRadius: 9,
        padding: isSuperView ? "12px 0px 12px 14px" : "30px 0 10px 30px",
      }}
      gap={10}
    >
      {isSuperView ? <h2>Quick Notes</h2> : <h1>Quick Notes</h1>}

      <Flex
        col
        grow
        style={{
          overflowY: "scroll",
          paddingBottom: 50,
          marginRight: 4,
        }}
        gap={10}
      >
        {!pending.length && !completed.length && (
          <AnimateInFlex
            style={{ color: theme.gray[8] }}
            animation={ANIMATION_BY_KEY.fade}
            key="quick-notes-empty-state"
          >
            Add a note below!
          </AnimateInFlex>
        )}

        <Flex col gap={6}>
          {pending.map((note) => (
            <QuickNoteListItem
              item={note}
              key={note.id}
              onDelete={handleDelete}
              onComplete={handleComplete}
            />
          ))}
        </Flex>
        {!!completed.length && (
          <>
            <motion.h3
              layoutId="quick-notes-completed-header"
              key="quick-notes-completed-header"
            >
              Completed
            </motion.h3>
            <Flex col gap={6}>
              {completed.map((note) => (
                <QuickNoteListItem
                  item={note}
                  key={note.id}
                  onDelete={handleDelete}
                  onComplete={handleComplete}
                />
              ))}
            </Flex>
          </>
        )}
      </Flex>

      <QuickNotesInput onSubmit={handleSubmit} />
    </Flex>
  )
}

export default QuickNotes
